import React, { useState, useEffect } from "react";
import { getData, postData, putData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaTags,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle
} from "react-icons/fa";


function SkuCategoriesManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await getData('/api/admin/get_sku_categories');
      
      // Verificar si la respuesta es exitosa
      if (response && response.data && response.data.skuCategories) {
        setCategories(response.data.skuCategories);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error cargando categorías:', error);
      toastError('Error al cargar las categorías SKU');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Preparar datos según la estructura esperada por el backend
      const categoryData = {
        data: {
          category_name: formData.name,
          active: 1,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };

      if (editingCategory) {
        // Actualizar categoría existente
        categoryData.data.id = editingCategory.id;
        const response = await putData('/api/admin/update_sku_category', categoryData);
        
        if (response && response.data) {
          toastSuccess(`Categoría "${formData.name}" actualizada exitosamente`);
        }
      } else {
        // Crear nueva categoría
        const response = await postData('/api/admin/create_sku_category', categoryData);
        
        if (response && response.data) {
          toastSuccess(`Categoría "${formData.name}" creada exitosamente`);
        }
      }
      
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '' });
      await loadCategories();

    } catch (error) {
      console.error('Error guardando categoría:', error);
      toastError(editingCategory ? 'Error al actualizar la categoría' : 'Error al crear la categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.category_name || '',
      description: category.description || ''
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDelete = (category) => {
    setDeletingCategory(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingCategory) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo en lugar de eliminar físicamente
      const dataToSend = {
        data: {
          id: deletingCategory.id,
          category_name: deletingCategory.category_name,
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      await putData('/api/admin/update_sku_category', dataToSend);
      toastSuccess(`Categoría "${deletingCategory.category_name}" eliminada exitosamente`);
      setShowDeleteModal(false);
      setDeletingCategory(null);
      await loadCategories();
    } catch (error) {
      console.error('Error eliminando categoría:', error);
      toastError('Error al eliminar la categoría');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingCategory(null);
  };

  return (
    <div className="sku-categories-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaTags className="me-2" />
            Gestión de Categorías SKU
          </h5>
          <button
            className="btn btn-primary"
            onClick={handleNew}
            disabled={loading}
          >
            <FaPlus className="me-2" />
            Nueva Categoría
          </button>
        </div>
        
        <div className="card-body">
          {loading && categories.length === 0 ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        No hay categorías registradas
                      </td>
                    </tr>
                  ) : (
                    categories.map(category => (
                      <tr key={category.id}>
                        <td>#{category.id}</td>
                        <td>
                          <strong>{category.category_name}</strong>
                        </td>
                        <td>{category.description || '-'}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(category)}
                            disabled={loading}
                            title="Editar categoría"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(category)}
                            disabled={loading}
                            title="Eliminar categoría"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar categoría */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nombre *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      placeholder="Ej: Dormitorios, Living, etc."
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Descripción</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      disabled={loading}
                      placeholder="Descripción opcional de la categoría"
                    ></textarea>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.name.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingCategory ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar la categoría SKU <strong>"{deletingCategory?.category_name}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  La categoría será marcada como inactiva y no aparecerá en las opciones disponibles.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkuCategoriesManager;
